import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { router } from '~/routes/router';
import { objectKeys } from '~/utils/object-keys';

type RoutesById = keyof typeof router.routesById;

type SearchParams = {
  from?: RoutesById;
  saveDataInSearch?: boolean;
};

/** Query param state; with `saveDataInSearch` it reads and writes the URL. Routes own defaults and stripping. */
export function useSearchParams<T extends Record<string, string | string[] | undefined>>(searchParams?: SearchParams) {
  const { from, saveDataInSearch = true } = searchParams ?? {};

  const navigate = useNavigate();
  const params = useParams(from ? { from, strict: true } : { strict: false });
  const search = useSearch(from ? { from, strict: true } : { strict: false });

  // Stable serialization of URL search, changes only when the URL changes.
  const searchKey = saveDataInSearch ? JSON.stringify(search) : '';
  const prevSearchKeyRef = useRef(searchKey);

  const getMergedSearch = () => (saveDataInSearch ? { ...search } : {}) as T;

  const [currentSearch, setCurrentSearch] = useState<T>(getMergedSearch);

  const setSearch = (newValues: Partial<T>) => {
    const updatedSearch = { ...currentSearch, ...newValues };

    for (const key of objectKeys(updatedSearch)) {
      // Clear empty values; the route's zod defaults fill them back in on read
      if (updatedSearch[key] === '' || updatedSearch[key] === undefined) {
        updatedSearch[key] = undefined as T[keyof T];
        continue;
      }

      // Flatten array values into underscore-joined string
      if (!Array.isArray(updatedSearch[key])) continue;
      const arr = updatedSearch[key];
      updatedSearch[key] = (arr.length <= 1 ? arr[0] : arr.join('_')) as T[keyof T];
    }

    if (!Object.keys(updatedSearch).some((key) => updatedSearch[key] !== currentSearch[key])) return;

    setCurrentSearch(updatedSearch);
    if (saveDataInSearch) {
      navigate({
        replace: true,
        params,
        resetScroll: false,
        to: '.',
        search: (prev) => ({ ...prev, ...updatedSearch }),
      });
    }
  };

  useEffect(() => {
    if (!saveDataInSearch || searchKey === prevSearchKeyRef.current) return;
    prevSearchKeyRef.current = searchKey;
    setCurrentSearch(getMergedSearch());
  }, [searchKey]);

  return { search: currentSearch, setSearch };
}
